<template>
	<div class="workflowListWrapper">
		<workflowListSkeleton :items="3" :loading="loadingList" />
		<el-empty :image="require('../assets/empty.svg')" v-if="!filteredProxyList.length && !loadingList"
			description="No Workflows"></el-empty>
		<div v-if="!loadingList && filteredProxyList.length" :key="searchQuery + filteredProxyList.length"
			class="listWrapper">
			<LazyList defaultLoadingColor="#210f3c" :data="filteredProxyList" :itemsPerRender="10"
				containerClasses="list">
				<template v-slot="{ item }">
					<div
						:id="item.workflowId === tourWorkflowId ? 'wf-tour-created-workflow-row' : null"
						@click="
						(e) => {
							launchWorkflow(e, item);
						}
					"
						class="item"
					>
						<el-container>
							<div class="nodeIconList" :key="`${icon.name}_${i}`"
								v-for="(icon, i) in item.workflowIcons">
								<img v-if="icon.icon && !icon.placeholder && !icon.more" class="nodeIcon" :src="`data:image/${icon.iconMimeType === 'png' ? 'png' : 'svg+xml'};base64, ` +
									icon.icon
									" />
								<img v-if="icon.placeholder" class="nodeIcon" :src="icon.icon" />
								<div class="nodeIconMore" v-if="icon.more">
									<i class="el-icon-more" />
								</div>
							</div>
							<el-main style="padding: 0px; margin-left: 20px; margin-right: 20px">
								<h3>{{ item.name }}</h3>
								<p class="description">{{ item.description || "No description yet..." }}</p>
							</el-main>
							<div style="display: inline-flex; align-items: center">
								<p style="font-size: 12px">
									Access:
									<el-tag style="margin-top: 5px" size="small">{{
										item.access === "admin"
											? "Admin"
											: item.access === "read"
												? "Can View Only"
												: "Can edit"
									}}</el-tag>
								</p>
								<el-divider direction="vertical"></el-divider>
								<p style="font-size: 12px">
									Last edit:
									<el-tag style="margin-top: 5px" size="small">{{
										returnTimeDifference(new Date(), new Date(item.updatedAt))
									}}</el-tag>
								</p>
								<el-divider direction="vertical"></el-divider>
								<workflowActiveSwitchElement :key="item.workflowId" :disabled="item.access === 'read'"
									@updateReferenceWorkflow="workflowUpdated" :simplifiedError="true" :workflow="item"
									:workflowListActive="true" />
								<el-divider direction="vertical"></el-divider>
								<el-dropdown>
									<span class="el-dropdown-link">
										<i style="font-size: 18px" class="el-icon-more"></i>
									</span>
									<el-dropdown-menu slot="dropdown">
										<el-dropdown-item @click.native="deleteWorkflow(item.workflowId)"
											:disabled="item.isActive || item.access !== 'admin' ? true : false">Delete
											workflow</el-dropdown-item>
										<el-dropdown-item @click.native="publishWorkflow(item)"
											:disabled="item.access !== 'admin' ? true : false">Publish
											workflow</el-dropdown-item>
										<el-dropdown-item @click.native="launchCollaborationModal(item)"
											:disabled="item.access !== 'admin' ? true : false">Collaborate</el-dropdown-item>
										<el-dropdown-item @click.native="launchEditModal(item)"
											:disabled="item.access === 'read' ? true : false">Edit
											settings</el-dropdown-item>
										<el-dropdown-item @click.native="exportWorkflow(item)"
											:disabled="item.access === 'read' ? true : false">Export
											workflow</el-dropdown-item>
									</el-dropdown-menu>
								</el-dropdown>
							</div>
						</el-container>
					</div>
				</template>
			</LazyList>
		</div>
		<slideOutModal @close="
			() => {
				showUpdateWorkflowModal = false;
			}
		" :visible="showUpdateWorkflowModal">
			<createUpdateWorkflowForm :existingWorkflow="workflowToEdit" @operationComplete="workflowUpdated" :workflowItemList="true" />
		</slideOutModal>
		<regularModal :noBottomPadding="true" top="5vh" width="60%" @close="
			() => {
				showPublishWorkflowModal = false;
			}
		" :visible="showPublishWorkflowModal">
			<publishWorkflowForm :workflowToPublish="workflowToPublish" :versionId="workflowToPublish?.versionId"
				@operationComplete="templatePublished" />
		</regularModal>
		<regularModal :noBottomPadding="true" width="60%" @close="
			() => {
				showCollaborateModal = false;
			}
		" :visible="showCollaborateModal">
			<collaborateWorkflowForm :workflowToCollaborateOn="workflowToCollaborateOn" />
		</regularModal>
		<el-dialog title="Export Workflow" :visible.sync="showExportModal" width="420px" :close-on-click-modal="false">
			<div class="dialog-field">
				<label class="dialog-label" style="margin-bottom: 8px; display: block;">Select version</label>

				<el-select v-if="exportVersions" v-model="selectedExportVersion" placeholder="Select version"
					style="width: 100%">
					<el-option v-for="v in exportVersions" :key="v.versionId" :label="v.versionName && v.versionName !== v.version
						? `${v.versionName} (${v.version})`
						: v.version
						" :value="v.versionId" />
				</el-select>
			</div>

			<span slot="footer">
				<el-button @click="showExportModal = false">
					Cancel
				</el-button>
				<el-button type="primary" @click="confirmExportWorkflow" :disabled="!selectedExportVersion">
					Export
				</el-button>
			</span>
		</el-dialog>
		<el-dialog title="Select Version to Publish" :visible.sync="showPublishVersionModal" width="420px"
			:close-on-click-modal="false">
			<div class="dialog-field">
				<label class="dialog-label" style="margin-bottom: 8px; display: block;">Select version</label>

				<el-select v-model="selectedPublishVersion" placeholder="Select version" style="width: 100%">
					<el-option v-for="v in publishVersions" :key="v.versionId" :label="v.versionName && v.versionName !== v.version
						? `${v.versionName} (${v.version})`
						: v.version
						" :value="v.versionId" />
				</el-select>
			</div>

			<span slot="footer">
				<el-button @click="showPublishVersionModal = false">Cancel</el-button>
				<el-button type="primary" :disabled="!selectedPublishVersion" @click="confirmPublishVersion">
					Continue
				</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import _ from "lodash";
import nodeSvg from "../assets/node.svg";
import workflowListSkeleton from "./workflowListSkeleton.vue";
import slideOutModal from "./slideOutModal.vue";
import regularModal from "./regularModal.vue";
import createUpdateWorkflowForm from "./createUpdateWorkflowForm.vue";
import LazyList from "lazy-load-list/vue";
import workflowActiveSwitchElement from "../components/workflowActiveSwitchElement.vue";
import publishWorkflowForm from "../components/publishWorkflowForm.vue";
import { timeDifference } from ".././utils/time.js";
import { mapGetters, mapActions } from "vuex";
import collaborateWorkflowForm from "./collaborateWorkflowForm.vue";
export default {
	components: {
		slideOutModal,
		workflowListSkeleton,
		createUpdateWorkflowForm,
		LazyList,
		workflowActiveSwitchElement,
		regularModal,
		publishWorkflowForm,
		collaborateWorkflowForm,
	},
	props: {
		list: Array,
		searchQuery: String,
		filter: Array,
		tourWorkflowId: String,
	},
	data() {
		return {
			workflowToEdit: null,
			showUpdateWorkflowModal: false,
			showPublishWorkflowModal: false,
			showCollaborateModal: false,
			workflowToPublish: null,
			workflowToCollaborateOn: null,
			loadingList: true,
			loadingTimeOut: 1500,
			showExportModal: false,
			exportWorkflowTarget: null,
			exportVersions: [],
			selectedExportVersion: null,
			showPublishVersionModal: false,
			publishVersions: [],
			selectedPublishVersion: null,
		};
	},
	computed: {
		...mapGetters(["returnActiveWorkflow"]),
		proxyList() {
			let clonedList = _.cloneDeep(this.list);
			for (let i = 0; i < clonedList.length; i++) {
				const workflow = clonedList[i];
				if (workflow.workflowIcons.length < 3) {
					const difference = 3 - workflow.workflowIcons.length;
					for (let j = 0; j < difference; j++) {
						workflow.workflowIcons.push({ icon: nodeSvg, placeholder: true });
					}
				}
				let moreThanFive = workflow.workflowIcons.length > 5 ? true : false;
				workflow.workflowIcons.splice(5);
				if (moreThanFive) {
					workflow.workflowIcons.push({ more: true });
				}
			}
			this.clearLoading();
			return this.loadingList
				? []
				: [...clonedList].sort((wa, wb) => new Date(wb.updatedAt).getTime() - new Date(wa.updatedAt).getTime());
		},
		filteredProxyList() {
			let filtered = this.proxyList.filter((each) => {
				return (
					(this.filter.includes("Owned By Me") && each.access === "admin") ||
					(this.filter.includes("Shared With Me") &&
						(each.access === "read" || each.access === "read/write")) ||
					(this.filter.includes("Active") && each.isActive) ||
					(this.filter.includes("Inactive") && !each.isActive) ||
					(this.filter.includes("Can Edit") && (each.access === "admin" || each.access === "read/write")) ||
					(this.filter.includes("Can View Only") && each.access === "read")
				);
			});
			if (this.searchQuery) {
				if (this.filter.length) {
					return filtered.filter((workflow) => {
						return workflow.name.toLowerCase().includes(this.searchQuery.toLowerCase());
					});
				} else {
					return this.proxyList.filter((workflow) => {
						return workflow.name.toLowerCase().includes(this.searchQuery.toLowerCase());
					});
				}
			}
			return this.filter.length ? filtered : this.proxyList;
		},
	},
	watch: {
		filter: function () {
			this.loadingList = true;
		},
	},
	methods: {
		...mapActions(["returnSpecificWorkflowMETHOD", "changesToUsageCreditMETHOD", "fetchWorkflowVersionsMETHOD"]),
		launchCollaborationModal(workflow) {
			this.workflowToCollaborateOn = workflow;
			this.showCollaborateModal = true;
		},
		async publishWorkflow(workflow) {
			this.workflowToPublish = workflow;

			const workflowId = workflow.workflowId;
			this.publishVersions = await this.fetchWorkflowVersionsMETHOD(workflowId);

			if (!this.publishVersions || !this.publishVersions.length) {
				await this.openPublishForm({ workflowId });
				return;
			}

			const active =
				this.publishVersions.find(v => v.isActive) || this.publishVersions[0];

			this.selectedPublishVersion = active.versionId;
			this.showPublishVersionModal = true;
		},
		async confirmPublishVersion() {
			const workflowId = this.workflowToPublish.workflowId;
			const versionId = this.selectedPublishVersion;

			this.showPublishVersionModal = false;

			await this.openPublishForm({ workflowId, versionId });
		},
		async openPublishForm({ workflowId, versionId = null }) {
			await this.returnSpecificWorkflowMETHOD({
				workflowId,
				versionId,
			});

			this.workflowToPublish = _.cloneDeep(this.returnActiveWorkflow);
			this.workflowToPublish.versionId = versionId || null;

			this.showPublishWorkflowModal = true;
		},
		returnTimeDifference(now, date) {
			return timeDifference(now, date);
		},
		launchWorkflow(e, workflow) {
			if (
				!e.target.classList.contains("el-switch__core") &&
				!e.target.classList.contains("el-icon-more") &&
				!e.target.parentElement.classList.contains("el-switch__label")
			) {
				if (localStorage.getItem("workflowTourInProgress") === "true") {
					localStorage.setItem("workflowTourPhase", "editor");
				}
				this.$router.push(`/workflows/${workflow.workflowId}/${workflow.name}`).catch(() => { });
			}
		},
		async exportWorkflow(workflow) {
			this.exportWorkflowTarget = workflow;
			const workflowId = workflow.workflowId;

			this.exportVersions = await this.fetchWorkflowVersionsMETHOD(workflowId);

			if (!this.exportVersions || this.exportVersions.length === 0) {
				this.$confirm(
					"Do you wish to export this workflow. Continue?",
					"Export Workflow",
					{
						confirmButtonText: "Export",
						cancelButtonText: "Cancel",
						type: "warning",
					}
				)
					.then(() => {
						this.exportWorkflowFile({ workflowId });
					})
					.catch(() => { });
				return;
			}
			const active =
				this.exportVersions.find(v => v.isActive) || this.exportVersions[0];

			this.selectedExportVersion = active?.versionId || null;

			this.showExportModal = true;
		},
		async confirmExportWorkflow() {
			const workflowId = this.exportWorkflowTarget.workflowId;
			const versionId = this.selectedExportVersion;

			this.showExportModal = false;

			await this.exportWorkflowFile({ workflowId, versionId });
		},
		async exportWorkflowFile({ workflowId, versionId = null }) {
			const result = await this.httpHelper(
				"RUNTIME",
				"GET",
				"workflow/exportOne",
				versionId ? { workflowId, versionId } : { workflowId },
				null,
				{ responseType: "blob" }
			);

			if (result?.error) {
				this.$notifyError(this, result.error);
				return;
			}

			const url = window.URL.createObjectURL(
				new Blob([result.response], { type: "application/zip" })
			);

			const fileName = versionId
				? `workflow_${workflowId}_${versionId}.zip`
				: `workflow_${workflowId}.zip`;

			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", fileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			this.$notify({
				title: "Success",
				message: "Workflow exported",
				type: "success",
				position: "bottom-left",
			});
		},
		launchEditModal(workflow) {
			this.workflowToEdit = {
				workflowId: workflow.workflowId,
				workflowLastUpdated: workflow.updatedAt,
				name: workflow.name,
				description: workflow.description,
				timezone: workflow.settings.timezone,
				settings: workflow.settings,
				versionId: workflow.versionId
			};
			this.showUpdateWorkflowModal = true;
		},
		templatePublished() {
			this.showPublishWorkflowModal = false;
		},
		workflowUpdated(workflow) {
			this.showUpdateWorkflowModal = false;
			this.$emit("workflowUpdated", workflow);
		},
		deleteWorkflow(workflowId) {
			this.$confirm("Do you wish to permanently delete this workflow. Continue?", "Warning", {
				confirmButtonText: "OK",
				cancelButtonText: "Cancel",
				type: "warning",
			})
				.then(async () => {
					const result = await this.httpHelper("RUNTIME", "DELETE", "workflow/deleteOne", { workflowId });
					if (!result?.error) {
						this.changesToUsageCreditMETHOD();
						this.$emit("itemRemoved", workflowId);
						this.$notify({
							title: "Success",
							message: "Workflow deleted",
							type: "success",
							position: "bottom-left",
						});
					} else {
						this.$notifyError(this, result.error);
					}
				})
				.catch(() => { });
		},
		clearLoading() {
			setTimeout(() => {
				this.loadingList = false;
			}, this.loadingTimeOut);
		},
	},
};
</script>

<style scoped>
.workflowListWrapper {
	padding: 0px;
}

.listWrapper {
	height: calc(100vh - 225px);
}

.list {
	padding-top: 24px !important;
	max-height: calc(100vh - 251px);
	margin: 0;
}

.workflowListWrapper /deep/ .item {
	background-color: white;
	height: 70px;
	box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
	border-radius: 4px;
	padding: 20px;
	display: flex;
	align-items: center;
	cursor: pointer;
	margin-bottom: 20px;
	margin-left: 2px;
	width: 94%;
}

.nodeIcon {
	height: 30px;
	width: 30px;
	border: 1px solid #210f3c;
	border-radius: 4px;
	margin-right: 5px;
}

.nodeIconList {
	display: inline-flex;
	align-items: center;
}

h3,
.description {
	margin-top: 7px;
	margin-bottom: 7px;
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.nodeIconMore {
	height: 30px;
	border: 1px solid #210f3c;
	border-radius: 4px;
	margin-right: 5px;
	width: 30px;
	display: flex;
}

.nodeIconMore i {
	margin: auto;
	pointer-events: none;
}
</style>
